/**
 * Kleine Bilder fuer die Woelbungsstufen im Relief-Hilfefenster.
 *
 * Gezeigt wird jeweils ein Querschnitt durch die Flaeche: unten der Boden,
 * oben die Gelaendeoberflaeche. Das passt fuer BEIDE Spalten der Tabelle,
 * nur die Schnittrichtung ist eine andere: Die vertikale Woelbung schneidet
 * in Gefaellrichtung (Kuppe oder Mulde im Hangprofil), die horizontale quer
 * dazu (Ruecken oder Tiefenlinie).
 *
 * Die drei Formen sind absichtlich Spiegelbilder voneinander (Rand 8 /
 * Mitte 3 gegen Rand 3 / Mitte 8, dazwischen die gerade Linie bei 5,5) -
 * so sieht man den Unterschied auch aus dem Augenwinkel.
 */
export type WoelbungForm = 'konvex' | 'gestreckt' | 'konkav';

/** Zeichenflaeche der Symbole (Breite x Hoehe). */
export const WOELBUNG_SYMBOL_VIEWBOX = '0 0 24 14';

const LINIEN: Record<WoelbungForm, string> = {
	// aufgewoelbt: die Oberflaeche steigt zur Mitte an (Kuppe, Ruecken)
	konvex: 'M2 12 L2 8 Q12 -2 22 8 L22 12 Z',
	// gerade: weder aufgewoelbt noch eingesenkt
	gestreckt: 'M2 12 L2 5.5 L22 5.5 L22 12 Z',
	// eingesenkt: die Oberflaeche faellt zur Mitte ab (Mulde, Tiefenlinie)
	konkav: 'M2 12 L2 3 Q12 13 22 3 L22 12 Z',
};

/**
 * Gibt den Linienzug zum Wort zurueck, sonst `null`.
 * `null` heisst: An dieser Stelle gehoert kein Bild hin (jede andere
 * Relief-Tabelle bleibt damit unveraendert).
 */
export function woelbungSymbol(wert: unknown): string | null {
	if (typeof wert !== 'string') return null;
	return LINIEN[wert.trim().toLowerCase() as WoelbungForm] ?? null;
}

/* ------------------------------------------------------------------ *
 * Schnittrichtung fuer die beiden Spaltenkoepfe
 * ------------------------------------------------------------------ */

/**
 * Kleines Bild fuer die Spaltenkoepfe „Vertikal“ und „Horizontal“.
 *
 * Gezeigt wird ein Hang von oben mit drei Hoehenlinien und der Linie, an der
 * geschnitten wird:
 *   vertikal   = den Hang hinab, QUER zu den Hoehenlinien,
 *   horizontal = ENTLANG einer Hoehenlinie, quer zum Hang.
 *
 * Deshalb ist die dicke Linie beim waagerechten Fall genau eine der drei
 * Hoehenlinien - das ist die ganze Aussage des Bildes.
 */
export type Schnittrichtung = 'vertikal' | 'horizontal';

/** Zeichenflaeche der Schnittrichtungs-Bilder (Breite x Hoehe). */
export const SCHNITT_SYMBOL_VIEWBOX = '0 0 22 16';

/** Die drei Hoehenlinien (Draufsicht), gleich fuer beide Bilder. */
export const SCHNITT_HOEHENLINIEN = 'M1 5 Q11 0 21 5 M1 10 Q11 5 21 10 M1 15 Q11 10 21 15';

const SCHNITTE: Record<Schnittrichtung, string> = {
	// quer zu den Hoehenlinien, also den Hang hinab
	vertikal: 'M11 1.5 L11 15.5',
	// deckungsgleich mit der mittleren Hoehenlinie, also quer zum Hang
	horizontal: 'M1 10 Q11 5 21 10',
};

/**
 * Gibt den Linienzug der Schnittlinie zum Spaltenkopf zurueck, sonst `null`.
 * `null` heisst wieder: Hier gehoert kein Bild hin.
 */
export function schnittrichtungSymbol(spaltenkopf: unknown): string | null {
	if (typeof spaltenkopf !== 'string') return null;
	return SCHNITTE[spaltenkopf.trim().toLowerCase() as Schnittrichtung] ?? null;
}
